import type { MeeEnv } from "src/config/env";

/**
 * Per-deployment product branding — the name and logo a stack presents.
 *
 * The backend serves it on `/env-config.js` (`BRAND_*` keys) from the client's
 * brand folder, which the deploy job overlays into `wwwroot/branding/` (see
 * `branding/README.md` at the repo root). Everything in the SPA that says the
 * product's name, or shows its logo, reads it from here rather than spelling
 * "Mee" out — the defaults below ARE the Mee brand, so a stack with no brand
 * keys is a Mee stack and the existing specs need no setup.
 *
 * A plain function, not a hook: `window._env_` is fixed for the page's
 * lifetime, and reading it late lets specs set `BRAND_*` before rendering.
 */
export type LogoStyle = "wordmark" | "static";

export interface Brand {
	/** "Mee", "Loop" — used in titles and copy ("Back to Loop"). */
	productName: string;
	/**
	 * Strapline after the name in the consumer title and on sign-in. Empty on a
	 * branded stack that has none: the sign-in line is then not rendered and the
	 * title is the product name alone.
	 */
	tagline: string;
	/**
	 * `wordmark` draws the built-in Mee letters (inline SVG, tinted by the
	 * organisation's brand text colour); `static` draws the client's own
	 * `/branding/logo.svg` untinted. Same vocabulary as the mobile apps.
	 */
	logoStyle: LogoStyle;
	/** The client's logo file (the Mee wordmark on a Mee stack). */
	logoUrl: string;
	/** 192px app icon — browser notification icon. */
	iconUrl: string;
	/** Consumer browser title: "Mee - My Employee Experience". */
	consumerTitle: string;
	/** Admin app name: "Mee Admin". */
	adminName: string;
	/** Admin browser title suffix: "Mee - Admin". */
	adminTitle: string;
}

const DEFAULT_PRODUCT_NAME = "Mee";
const DEFAULT_TAGLINE = "My Employee Experience";

export function getBrand(): Brand {
	const env = (window._env_ ?? {}) as Partial<MeeEnv>;
	const productName = env.BRAND_PRODUCT_NAME?.trim() || DEFAULT_PRODUCT_NAME;
	// Absent key = a stack with no brand keys at all (Mee). An empty string is a
	// deliberate "no strapline" from the brand file.
	const tagline =
		env.BRAND_TAGLINE === undefined
			? DEFAULT_TAGLINE
			: env.BRAND_TAGLINE.trim();
	const logoStyle: LogoStyle =
		env.BRAND_LOGO_STYLE === "static" ? "static" : "wordmark";
	return {
		productName,
		tagline,
		logoStyle,
		logoUrl: "/branding/logo.svg",
		iconUrl: "/branding/icon-192.png",
		consumerTitle: tagline ? `${productName} - ${tagline}` : productName,
		adminName: `${productName} Admin`,
		adminTitle: `${productName} - Admin`
	};
}
